import type React from 'react';
import type { Route } from 'components/dynamic-page/route-matchers/types';
import type { ReadonlyDeep } from 'type-fest';
import type { CountryCode, CountryCodeList } from 'types/countries';
import type { SiteLanguage } from 'types/locales';
import type { Slide } from 'components/autoplay-slides';
import { HtmlTransClassNames } from 'components/html-trans';
import { joinExpCaPaths, joinExpUsPaths } from 'types/locales';
import { ThemeConfig, ThemeNames } from './theme';
import type { CurrencyCode } from 'types/currencies';
import type { ConsentRule } from 'domain/consent';
import type { NextRequest, NextResponse } from 'next/server';

/**
 * Tenant Localization Config
 * 
 * Defines language, country, and currency settings for a tenant.
 */
export interface TenantLocalizationConfig {
  /** Languages supported by the tenant */
  languages: (string | SiteLanguage)[];
  /** Countries the tenant operates in */
  countries: (string | CountryCode)[];
  /** i18n namespaces required (files in public/locales) */
  namespaces: Namespaces[];
  /** Default language for the tenant */
  defaultLanguage: SiteLanguage;
  /** Default country for the tenant */
  defaultCountry: CountryCode;
  /** Whether locale switching is enabled */
  switchingEnabled?: boolean;
  /** Whether to persist locale preference */
  persistLocalePreference?: boolean;
  /** Whether global locale switching features are enabled */
  allowGlobalLocaleSwitching?: boolean;
  /** Maps supported countries to their currencies */
  currencyMap: Record<CountryCode, CurrencyCode>;
}
import type { Namespaces } from 'types/locales';
import type { TiltedCardsProps } from 'components/tilted-cards';
import type { GetInTouchProps } from 'components/commercial/get-in-touch';
import type { InternationalRegion } from 'components/modal-dialog/international-links';
import { AllowedExperimentKey } from './experiments';

//#region Tenant IDs
export const EXP_COMMERCIAL_TENANT_ID = 'expCommercialUs' as const;
export const EXP_COMMERCIAL_CA_TENANT_ID = 'expCommercialCa' as const;
export const EXP_IN_SERVICE_TENANT_ID = 'expInService' as const;
export const EXP_REALTY_CA_TENANT_ID = 'expRealtyCa' as const;
export const EXP_REALTY_US_TENANT_ID = 'expRealtyUs' as const;
export const ZOOCASA_TENANT_ID = 'zoocasa' as const;
export const AGNT_INC_TENANT_ID = 'agntInc' as const;
//#endregion

export type EXP_COMMERCIAL_TENANT_ID_TYPE = typeof EXP_COMMERCIAL_TENANT_ID;
export type EXP_COMMERCIAL_CA_TENANT_ID_TYPE = typeof EXP_COMMERCIAL_CA_TENANT_ID;
export type EXP_IN_SERVICE_TENANT_ID_TYPE = typeof EXP_IN_SERVICE_TENANT_ID;
export type EXP_REALTY_CA_TENANT_ID_TYPE = typeof EXP_REALTY_CA_TENANT_ID;
export type EXP_REALTY_US_TENANT_ID_TYPE = typeof EXP_REALTY_US_TENANT_ID;
export type ZOOCASA_TENANT_ID_TYPE = typeof ZOOCASA_TENANT_ID;
export type AGNT_INC_TENANT_ID_TYPE = typeof AGNT_INC_TENANT_ID;

export type TenantId =
  | ZOOCASA_TENANT_ID_TYPE
  | EXP_IN_SERVICE_TENANT_ID_TYPE
  | EXP_REALTY_CA_TENANT_ID_TYPE
  | EXP_REALTY_US_TENANT_ID_TYPE
  | EXP_COMMERCIAL_TENANT_ID_TYPE
  | EXP_COMMERCIAL_CA_TENANT_ID_TYPE
  | AGNT_INC_TENANT_ID_TYPE;

export const TenantIds = {
  ZOOCASA: ZOOCASA_TENANT_ID,
  EXP_IN_SERVICE: EXP_IN_SERVICE_TENANT_ID,
  EXP_REALTY_CA: EXP_REALTY_CA_TENANT_ID,
  EXP_REALTY_US: EXP_REALTY_US_TENANT_ID,
  EXP_COMMERCIAL: EXP_COMMERCIAL_TENANT_ID,
  EXP_COMMERCIAL_CA: EXP_COMMERCIAL_CA_TENANT_ID,
  AGNT_INC: AGNT_INC_TENANT_ID,
} as const;

export type FooterColItem = {
  label: TranslationConfig;
  link?: string;
  target?: string;
  isAnchor?: boolean;
};

export type FooterImageData = {
  src: string;
  alt: TranslationConfig;
};

export type ExpFooterComponentProps = {
  config: FooterConfig;
};

export type TranslationConfig = {
  key: string;
  defaultValue: string;
};

export type HeaderConfig = ReadonlyDeep<{
  mainButtonCopy: TranslationConfig;
  authButtonCopy: TranslationConfig;
  internationalButtonCopy?: TranslationConfig;
  hasJoinExpPage: boolean;
  hasAdvisorSignIn?: boolean;
  authMenuItems: {
    label: TranslationConfig;
    link?: string;
    id: string;
    signOut?: boolean;
  }[];
  menuItems: {
    label: TranslationConfig;
    link?: string;
    id: string;
    newTab?: boolean;
    mobileOnly?: boolean;
  }[];
  buttonLabels?: {
    commercialWaitlist?: TranslationConfig;
    imReady?: TranslationConfig;
    contactUs?: TranslationConfig;
    mainButton?: TranslationConfig;
    authButton?: TranslationConfig;
  };
}>;

export type ContentCard = {
  title: string;
  image: string;
  titleColor: string;
  backgroundColor: string;
  content: string;
  link: string;
  desktopTransform?: string;
  tabletTransform?: string;
  translateYDesktop?: string;
  translateYTablet?: string;
  rounded?: boolean;
  altText?: string;
  buttonText?: string;
  hasDynamicLink?: boolean;
};

export type TestimonialProps = {
  text: string;
  image: string;
  alt: string;
  name: string;
  company: string;
  index: number;
};

export type InternalLinks = {
  title: string;
  links: {
    label: string;
    link: string;
  }[];
};

export type HomeConfig = ReadonlyDeep<{
  heroVideo?: string;
  heroImage?: string;
  heroImageAlt?: string;
  joinLink?: string;
  testimonialBlock?: {
    backgroundImage: string;
    heading: string;
    testimonials: TestimonialProps[];
  };
  joinUsSection?: {
    link: string;
    title: string;
    title2: string;
    description: string;
    imageUrl: string;
    buttonText: string;
  };
  contentCards?: {
    sectionTitleParts: string[];
    cards: ContentCard[];
  };
  carouselImages?: string[];
  internalLinks?: InternalLinks[];
  autoplaySlides?: {
    title: string;
    description: string;
    linkText: string;
    linkPath: string;
    slides: Slide[];
  };
  tiltedCards?: TiltedCardsProps;
}>;

export type AddressOptions = {
  siteLocation?: CountryCodeList;
  locale?: string;
  isUserInQuebec?: boolean;
};

export type InternationalLinksConfig = ReadonlyDeep<{
  title: string;
  subtitle: string;
  linkData: InternationalRegion[];
}>;

export type OrganizationConfig = ReadonlyDeep<{
  phoneNumber: (route?: Route) => string | null;
  email: (route?: Route) => string | null;
  address: (options?: AddressOptions) => string | null;
  companyInfo1?: string;
  companyInfo2?: string;
  twitterUrl: string;
  facebookUrl: string;
  instagramUrl: string;
  linkedinUrl: string;
  youtubeUrl?: string;
  joinSectionUrl?: string;
  internationalLinks?: InternationalLinksConfig;
}>;

export type FooterConfig = ReadonlyDeep<{
  footerColumns?: {
    column1?: FooterColItem[];
    column2?: FooterColItem[];
    column3?: FooterColItem[];
    bottom?: FooterColItem[][];
    joinUsLink?: string;
    footerImageData?: FooterImageData;
  };
  showAccessibilityLink?: boolean;
}>;

export type AgentSearchConfig = ReadonlyDeep<{
  headerImage: {
    mobileSrc: string;
    src: string;
    alt: string;
  };
  agentTypeName: string;
  featuredAgentDescriptor: string;
  pageTitle: string;
  showCountrySelect?: boolean;
}>;

export type ContactUsConfig = ReadonlyDeep<{
  title: string;
  description: string;
  notificationContent: string;
  bottomContent: {
    title: string;
    description: string;
  };
  hubspotFormId: string;
}>;

export type CommercialBenefitsCard = {
  title?: string;
  titleHtml?: string;
  description: string;
  type: 'card';
};

export type CommercialBenefitCardMedia = {
  url: string;
  type: 'image';
  altText: string;
};

export type CommercialBenefits = ReadonlyDeep<{
  joinUsLink: string;
  benefits: (CommercialBenefitsCard | CommercialBenefitCardMedia)[];
}>;

export type CommercialMetricsConfig = ReadonlyDeep<{
  align?: 'left' | 'center';
  showNasdaq?: boolean;
  title?: string;
  metrics: CommercialMetrics[];
  backgroundVideo: {
    src: string;
    type: string;
  };
  logo: {
    src: string;
    alt: string;
    width: number;
    height: number;
  };
}>;

export type CommercialMetrics = {
  value: string;
  label: string;
};

export type SearchOurProperties = ReadonlyDeep<{
  // TODO: When we have listings available, make these fields required
  title?: {
    __html: string;
  };
  linkText?: string;
  linkPath?: string;
  imageCarousel: string[];
}>;

export type ExpCommercialConfig = ReadonlyDeep<{
  quotes: {[authorId: string]: Quote };
  metrics: CommercialMetricsConfig;
  benefits: CommercialBenefits;
  searchOurProperties: SearchOurProperties;
  faq: FAQ;
  listing: CommercialListingConfig;
  getInTouch: GetInTouchProps;
}>;

const MY_LINK_MY_LEAD_FEATURE_NAME = 'MyLinkMyLead' as const;
const AGENT_SEARCH_FEATURE_NAME = 'AgentSearch' as const;
const ACCESSIBILITY_WIDGET_FEATURE_NAME = 'AccessibilityWidget' as const;
const TRUSTARC_FEATURE_NAME = 'TrustArc' as const;
const ADDRESS_PAGE_FEATURE_NAME = 'AddressPage' as const;
const SHOW_PROFILE_LOCALE_SWITCHER_FEATURE_NAME = 'ShowProfileLocaleSwitcher' as const;
const HAS_MULTILINGUAL_CONTENT_PAGES_FEATURE_NAME = 'HasMultilingualContentPages' as const;
const SHOW_HYBRID_AREA_MAP_PAGE_FEATURE_NAME = 'ShowHybridAreaMapPage' as const;
const USE_LISTING_CARD_V2_FEATURE_NAME = 'UseListingCardV2' as const;
const SHOW_QUEBEC_POPUP_FEATURE_NAME = 'ShowQuebecPopup' as const;
const AI_SEARCH_FEATURE_NAME = 'AiSearch' as const;
const SHOW_AGENT_CARDS_ON_ADDRESS_PAGE_FEATURE_NAME = 'ShowAgentCardsOnAddressPage' as const;
const SHOWCASE_FEATURE_NAME = 'Showcase' as const;
const USE_NEW_AREA_SEARCH_DESIGN_FEATURE_NAME = 'UseNewAreaSearchDesign' as const;
const PROPERTY_PREVIEW_DRAWER_FEATURE_NAME = 'PropertyPreviewDrawer' as const;
const USE_NEW_HYBRID_PAGE_DESIGN_FEATURE_NAME = 'UseNewHybridPageDesign' as const;
/** AreaPageV2 shell redesign. Supported on Zoocasa, eXp US, and eXp CA. **/
const USE_AREA_PAGE_V2_FEATURE_NAME = 'UseAreaPageV2' as const;
/** Natural-language search on the AreaPageV2 search bar. eXp CA only for now. **/
const USE_SMART_SEARCH_FEATURE_NAME = 'UseSmartSearch' as const;
const USE_BUILDING_GROUPED_PINS_FEATURE_NAME = 'UseBuildingGroupedPins' as const;
const EXPERIMENTS_FEATURE_NAME = 'Experiments' as const;
const USE_ZOOCASA_LIGHT_HOMEPAGE_FEATURE_NAME = 'UseZoocasaLightHomepage' as const;
const USE_LOCAL_FOCUS_HOMEPAGE_FEATURE_NAME = 'UseLocalFocusHomepage' as const;
const POI_AMENITIES_FILTER_FEATURE_NAME = 'PoiAmenitiesFilter' as const;
const LOGIN_ROLE_SELECTION_FEATURE_NAME = 'LoginRoleSelection' as const;
const LISTING_PROPERTY_DETAILS_REDESIGN_FEATURE_NAME = 'ListingPropertyDetailsRedesign' as const;
const USE_DETAILED_RECENT_SEARCHES_FEATURE_NAME = 'UseDetailedRecentSearches' as const;

export type ShowcaseFeature = {
  /** Determines if RESO listings can be displayed and/or searched in this tenant. Default is `true` */
  resoEnabled?: boolean;
  /** Determines if eXp Commercial listings can be displayed and/or searched in this tenant. Default is `false` */
  expCommercialEnabled?: boolean;
};

export const DEFAULT_SHOWCASE_FEATURE: ShowcaseFeature = {
  resoEnabled: true,
  expCommercialEnabled: false,
} as const;

//#region Address Page Config

export const TENANT_CONFIG_ADDRESS_PAGE_KEY = 'addressPage' as const;

/** Page layout version - controls overall page structure */
export type PageLayoutVersion = 'v1' | 'v2';

/**
 * Layout configuration with optional mobile/desktop separation
 * 
 * Examples:
 * - Single layout for all: `layout: 'v2'`
 * - Different layouts: `layout: { mobile: 'v1', desktop: 'v2' }`
 */
export type LayoutConfig = PageLayoutVersion | {
  mobile: PageLayoutVersion;
  desktop: PageLayoutVersion;
};

export type AddressPageConfig = ReadonlyDeep<{
  /** 
   * Page layout version
   * - v1: Legacy layout (carousel hero, two-column content section)
   * - v2: Redesign (bento gallery, sticky sidebar, new header)
   * 
   * Can be a single version or separate mobile/desktop versions:
   * - `'v2'` - same layout for mobile and desktop
   * - `{ mobile: 'v1', desktop: 'v2' }` - different layouts
   */
  layout?: LayoutConfig;
}>;

export type TenantConfigAddressPage = { [TENANT_CONFIG_ADDRESS_PAGE_KEY]: AddressPageConfig };

//#endregion

export type Features = {
  [MY_LINK_MY_LEAD_FEATURE_NAME]?: boolean;
  [AGENT_SEARCH_FEATURE_NAME]?: boolean;
  /** Determines if the accessibility widget should be shown for a given tenant. Default is `false` */
  [ACCESSIBILITY_WIDGET_FEATURE_NAME]?: boolean;
  /** Determines if the TrustArc script should be included for a given tenant. Default is `false` */
  [TRUSTARC_FEATURE_NAME]?: {
    enabled: boolean;
    domain: string;
  };
  [ADDRESS_PAGE_FEATURE_NAME]?: {
    /** Determines if the neighbourhood demographics should be shown for a given tenant. Default is `false` */
    showNeighbourhoodDemographics?: boolean;
    /** Determines if the schools should be shown for a given tenant. Default is `false` */
    showSchools?: boolean | ((listingCountryCode: CountryCode, siteCountryCode: CountryCodeList, userCountryCode: CountryCode) => boolean);
    /** Determines if the travel time should be shown for a given tenant. Default is `false` */
    showTravelTime?: boolean;
    /** Determines if the walk score should be shown for a given tenant. Default is `true` */
    showWalkScore?: boolean | ((listingCountryCode: CountryCode, siteCountryCode: CountryCodeList, userCountryCode: CountryCode) => boolean);
    /** Determines if the property appraisal should be shown for a given tenant. Default is `true` */
    showPropertyAppraisal?: boolean | ((listingCountryCode: CountryCode, siteCountryCode: CountryCodeList, userCountryCode: CountryCode) => boolean);
    /** Determines if the demographics should be shown for a given tenant. Default is `false` */
    showDemographics?: boolean | ((listingCountryCode: CountryCode, siteCountryCode: CountryCodeList, userCountryCode: CountryCode) => boolean);
  };
  [SHOW_PROFILE_LOCALE_SWITCHER_FEATURE_NAME]?: boolean;
  [HAS_MULTILINGUAL_CONTENT_PAGES_FEATURE_NAME]?: boolean;
  [SHOW_HYBRID_AREA_MAP_PAGE_FEATURE_NAME]?: boolean;
  /**
   * Determines if ListingCard with external compliance resolution should be used for this tenant.
   * When enabled, uses view models that follow the similar-listings pattern with compliance handled externally.
   * Default is `false`
   */
  [USE_LISTING_CARD_V2_FEATURE_NAME]?: boolean;
  /** Determines if the Quebec popup should be shown when non-Quebec users view Quebec content. Default is `false` */
  [SHOW_QUEBEC_POPUP_FEATURE_NAME]?: boolean;
  [AI_SEARCH_FEATURE_NAME]?: boolean;
  /** Determines if the agent cards should be shown on the address/listing page for a given tenant. Default is `false` */
  [SHOW_AGENT_CARDS_ON_ADDRESS_PAGE_FEATURE_NAME]?: boolean;
  /** Determines the listing types that can be displayed and/or searched in this tenant. Default is {@link DEFAULT_SHOWCASE_FEATURE} */
  [SHOWCASE_FEATURE_NAME]?: ShowcaseFeature;
  /** Determines if the new area search design should be used for a given tenant. Default is `false` */
  [USE_NEW_AREA_SEARCH_DESIGN_FEATURE_NAME]?: boolean;
  /** Determines if the property preview drawer should be shown on mobile map views. Only renders on v2 of the search page and hybrid page (`UseNewAreaSearchDesign` / `UseNewHybridPageDesign`). Default is `false` */
  [PROPERTY_PREVIEW_DRAWER_FEATURE_NAME]?: boolean;
  /** Determines if the new hybrid page design should be used for a given tenant. Default is `false` */
  [USE_NEW_HYBRID_PAGE_DESIGN_FEATURE_NAME]?: boolean;
  /**
   * AreaPageV2 shell redesign. Requires `ShowHybridAreaMapPage` to be enabled for the tenant.
   * Enabled for Zoocasa (rollout owned by the `dj-area-page-redesign` GrowthBook experiment),
   * eXp US, and eXp CA. Default is `false`.
   */
  [USE_AREA_PAGE_V2_FEATURE_NAME]?: boolean;
  /**
   * Natural-language ("smart") search on the AreaPageV2 search bar. Only takes
   * effect where that search bar renders, so it requires `UseAreaPageV2`.
   * Enabled for eXp CA only. Default is `false`.
   */
  [USE_SMART_SEARCH_FEATURE_NAME]?: boolean;
  /** Determines if building-grouped map pins replace geohash clusters for a given tenant. Default is `false` */
  [USE_BUILDING_GROUPED_PINS_FEATURE_NAME]?: boolean;
  /** Determines if the zoocasa-light homepage and header should be shown for the zoocasa tenant. Default is `false` */
  [USE_ZOOCASA_LIGHT_HOMEPAGE_FEATURE_NAME]?: boolean;
  /** Determines if the local-focus homepage should be used for the eXp US tenant (instead of the legacy eXp US homepage). Default is `false` */
  [USE_LOCAL_FOCUS_HOMEPAGE_FEATURE_NAME]?: boolean;
  /** Enable POI amenities filter panel is available on the map search page. Default is `false` */
  [POI_AMENITIES_FILTER_FEATURE_NAME]?: boolean;
  /** Determines if the login modal opens with a role-selection screen (Agent / Buyer or Seller / Other) before sign-in, when launched from the header. Default is `false` */
  [LOGIN_ROLE_SELECTION_FEATURE_NAME]?: boolean;
  /** Determines if the redesigned single "Property Details" tab (Description + Key Facts + Additional Information sourced from locale data) is shown on the address page. Default is `false` */
  [LISTING_PROPERTY_DETAILS_REDESIGN_FEATURE_NAME]?: boolean;
  /** Determines if recent searches capture and display the active filter (beds/price/home type) alongside the location, and allow restoring it. Default is `false` */
  [USE_DETAILED_RECENT_SEARCHES_FEATURE_NAME]?: boolean;
  /**
   * Experiment configuration for this tenant.
   * Maps experiment keys to their configuration.
   * 
   * @example
   * ```ts
   * Experiments: {
   *   [AllowedExperimentKey.LISTING_HERO]: {
   *     enabled: true,
   *     observability: true, // Enable verbose logging for debugging
   *   },
   *   [AllowedExperimentKey.SEARCH_FILTERS]: {
   *     enabled: true,
   *     observability: false, // No verbose logging
   *   },
   * }
   * ```
   */
  [EXPERIMENTS_FEATURE_NAME]?: Partial<Record<AllowedExperimentKey, {
    /**
     * Whether this experiment is enabled for this tenant.
     * Provides code-level safety layer on top of GrowthBook targeting.
     * If false or not configured, always shows control variant.
     */
    enabled: boolean;
    /**
     * Whether to enable verbose observability logging for this experiment.
     * Useful for debugging specific experiments without flooding logs.
     * Optional - defaults to false.
     */
    observability?: boolean;
  }>>;
};

export type Quote = ReadonlyDeep<{
  authorImage: string;
  authorImageAlt: string;
  authorTitle: string;
  authorName: string;
  quoteContent: string;
  highlightColor?: string;
  className?: HtmlTransClassNames;
}>;

export type FAQ = ReadonlyDeep<{
  title: string;
  content: FaqItem[];
}>;

export type FaqItem = ReadonlyDeep<{
  title: string;
  children: string;
  styleOverrides?: Record<string, any>;
  values?: Record<string, any>;
}>;

export type CommercialListingConfig = ReadonlyDeep<{
  forSale: string;
  forLease: string;
  fullAddress: string;
  price: string;
  propertyType: string;
  yearBuilt: string;
  highlights: string;
  propertyDetails: {
    title: string;
    status: string;
    salePrice: string;
    salePricePerUnit: string;
    yearBuilt: string;
    propertyType: string;
    buildingSize: string;
    lotSize: string;
    capRate: string;
    stories: string;
    buildingClass: string;
    occupancyPct: string;
    zoningDescription: string;
    daysOnMarket: string;
  };
  propertyDescription: string;
  locationDescription: string;
  viewMore: string;
  viewLess: string;
  requestMoreInfo: string;
  shareToSocial: string;
}>;

export type VideoCardData = {
  videoId: string;
  bottomText: string;
};

export type CardStackData = ReadonlyDeep<{
  title: string;
  content: CardStackItem[];
}>;

export type CardStackItem = ReadonlyDeep<{
  title: string;
  description: string;
  imageSrc: string;
  backgroundColor: string;
  textColor: string;
  imageAlt: string;
  /** Optional CTA link. When present, a split-pill "ctaLabel → " button is rendered. */
  href?: string;
  /** CTA button label. Defaults to "Learn more" when omitted. */
  ctaLabel?: string;
  /** CSS background-position for the card image, e.g. "center 35%". Defaults to "center". */
  imagePosition?: string;
}>;

export type ProgramData = ReadonlyDeep<{
  image: string;
  imageAlt: string;
  title: string;
  description: string;
  buttonText: string;
  buttonLink: string;
  buttonClickEvent?: string;
}>;

export type PowerOfExpRegion = {
  id: string;
  title: joinExpCaPaths | joinExpUsPaths;
  imageSrc: joinExpCaPaths | joinExpUsPaths;
  url: joinExpCaPaths | joinExpUsPaths;
};

export type PowerOfExpData = ReadonlyDeep<{
  title: string;
  expExplainedHashtag: string;
  section1: {
    image: string;
    imageAlt: string;
    title: string;
    info: string;
    timing: string;
    button: string;
    signMeUpHref: string;
  };
  section2: {
    image: string;
    imageAlt: string;
    title: {
      mobile: string;
      desktop: string;
    };
    info: string;
    button: {
      mobile: string;
      desktop: string;
    };
  };
  presentation: {
    modal: {
      title: string;
      downloadButton: string;
      regions: PowerOfExpRegion[];
    };
  };
}>;

export type BuiltForThisLogo = {
  src: string;
  alt: string;
  width?: number | `${number}` | undefined;
  height?: number | `${number}` | undefined;
};

export type BuiltForThisVideo = {
  src: string;
  type: string;
};

export type BuildForThisData = {
  label: string;
  clickEvent: string;
  logo: Record<SiteLanguage, BuiltForThisLogo>;
  video: Record<SiteLanguage, BuiltForThisVideo>;
};

// Define the config type
export type JoinExpConfig = ReadonlyDeep<{
  joinPageContactPage: string;
  joinPageContactPath: string;
  learnMoreClickEvent: string;
  signMeUpClickEvent: string;
  getNowClickEvent: string;
  fabDestination: string; // URL destination for the FAB
  fabClickEvent: string; // GTM event for FAB clicks
  quotes: {[authorId: string]: Quote };
  videoCardData: VideoCardData[];
  cardStackData: CardStackData;
  programsData: ProgramData[];
  faq: FAQ;
  powerOfExpData: PowerOfExpData;
  builtForThis: BuildForThisData;
}>;

export type SEOConfig = {
  noFollowRoutes?: Route[]; // Routes to apply nofollow metadata to
  noIndexRoutes?: Route[]; // Routes to apply noindex metadata to
  useLocaleAwareCanonicals?: boolean; // Controls whether canonical URLs should include locale prefixes for non-default locales (e.g., /fr-CA/)
  useLocaleAwareHrefLang?: boolean; // Controls whether to use locale-aware hreflang generation (default: false, uses legacy behavior)
  includeInLanguageInSchemas?: boolean; // Controls whether to add inLanguage property to JSON-LD schemas for SEO (default: false)
};

export type HeadTagsConfig = ReadonlyDeep<{
  /** Plain string for the default page title */
  defaultTitle: string;
  /** Translation key for the default meta description */
  defaultMetaDescriptionKey: string;
}>;

export type FiltersConfig = {
  // Determines whether the filter bar will allow users to filter for expired listings
  hideExpiredListings?: boolean;
  // This is used to set the country code for the filter bar.
  countryCode?: CountryCode;
  /**
   * Determines whether the "Show Coming Soon Listings" toggle appears in the filter panel.
   * Opt-in: enabled only for tenants with Coming Soon support (Zoocasa, eXp US, eXp In-Service);
   * hidden by default for everyone else (e.g. eXp Canada, Commercial).
   */
  showComingSoonFilter?: boolean;
};

export type ContentHostSelector = CountryCodeList | 'ALL';
export type ContentHosts = Record<ContentHostSelector, string>;

export type MobileAppBannerConfig = {
  name: string;
  appId: string;
} | null;

type RouteHandler = (
  req: NextRequest,
) => NextResponse | undefined | Promise<NextResponse | undefined>;

type RouteMatcher = string | RegExp;

type RouteHandlerEntry = {
  match: RouteMatcher;
  handler: RouteHandler;
};

type TenantRouteConfig = {
  claimed: '*' | string[];
  excluded?: string[];
  handlers?: RouteHandlerEntry[];
};

export const TENANT_CONFIG_HOME_KEY = 'home' as const;
export const TENANT_CONFIG_HEADER_KEY = 'header' as const;
export const TENANT_CONFIG_FOOTER_KEY = 'footer' as const;
export const TENANT_CONFIG_AGENT_SEARCH_KEY = 'agentSearch' as const;
export const TENANT_CONFIG_SEO_KEY = 'seo' as const;
export const TENANT_CONFIG_FILTERS_KEY = 'filters' as const;
export const TENANT_CONFIG_JOIN_EXP_KEY = 'joinExp' as const;
export const TENANT_CONFIG_EXP_COMMERCIAL_KEY = 'commercial' as const;
export const TENANT_CONFIG_CONTACT_US_KEY = 'contactUs' as const;
export const TENANT_CONFIG_ORGANIZATION_KEY = 'organization' as const;
export const TENANT_CONFIG_HEAD_TAGS_KEY = 'headTags' as const;
export const TENANT_CONFIG_NAVIGATION_KEY = 'navigation' as const;

export type TenantConfigHome = { [TENANT_CONFIG_HOME_KEY]: HomeConfig };
export type TenantConfigHeader = { [TENANT_CONFIG_HEADER_KEY]: HeaderConfig };
export type TenantConfigFooter = { [TENANT_CONFIG_FOOTER_KEY]: FooterConfig };
export type TenantConfigOrganization = { [TENANT_CONFIG_ORGANIZATION_KEY]: OrganizationConfig };
export type TenantConfigAgentSearch = { [TENANT_CONFIG_AGENT_SEARCH_KEY]: AgentSearchConfig };
export type TenantConfigSEO = { [TENANT_CONFIG_SEO_KEY]: SEOConfig };
export type TenantConfigFilters = { [TENANT_CONFIG_FILTERS_KEY]: FiltersConfig };
export type TenantConfigJoinExp = { [TENANT_CONFIG_JOIN_EXP_KEY]: JoinExpConfig };
export type TenantConfigExpCommercial = { [TENANT_CONFIG_EXP_COMMERCIAL_KEY]: ExpCommercialConfig };
export type TenantConfigContactUs = { [TENANT_CONFIG_CONTACT_US_KEY]: ContactUsConfig };
export type TenantConfigHeadTags = { [TENANT_CONFIG_HEAD_TAGS_KEY]: HeadTagsConfig };

/** Navigation config extended with optional triggers for customising button display for a given tenant */
export type TenantNavigationConfig = import('@core/ui/navigation').NavigationConfig & {
  menuTrigger?: React.ReactNode;
  mobileUserTrigger?: React.ReactNode;
  mobileMenuTrigger?: React.ReactNode;
  panelClassName?: string;
  useDropdownMenu?: boolean;
};
export type TenantConfigNavigation = { [TENANT_CONFIG_NAVIGATION_KEY]: TenantNavigationConfig };

/**
 * Observability configuration for tenant.
 * Controls tracking and monitoring features.
 */
export type ObservabilityConfig = {
  /** Master toggle for all observability features */
  enabled?: boolean;
  /** Area page specific observability settings */
  areaPages?: {
    /** Toggle for area page tracking */
    enabled?: boolean;
    /** List of critical city slugs (e.g., 'toronto-on', 'vancouver-bc') */
    criticalCities?: string[];
  };
};

export type TenantConfigObservability = { observability?: ObservabilityConfig };

export const DEFAULT_GTM_CONTAINER_ID_KEY = 'defaultContainerId' as const;

export type TenantConfig =
  | TenantConfigHome
  | TenantConfigHeader
  | TenantConfigFooter
  | TenantConfigOrganization
  | TenantConfigAgentSearch
  | TenantConfigSEO
  | TenantConfigFilters
  | TenantConfigJoinExp
  | TenantConfigExpCommercial
  | TenantConfigContactUs
  | TenantConfigHeadTags
  | TenantConfigAddressPage
  | TenantConfigObservability
  | TenantConfigNavigation
  | object;

export type Tenant<
  I extends TenantId,
  T extends ThemeConfig<ThemeNames>,
  C extends TenantConfig = object,
  G extends string = typeof DEFAULT_GTM_CONTAINER_ID_KEY
> = ReadonlyDeep<{
  /**
   * The ID of the tenant.
   *
   * This is used to identify the tenant.
   *
   * @example
   * ```ts
   * const tenant = getTenantById(ZOOCASA_TENANT_ID);
   * ```
   */
  id: I;

  /**
   * The list of hosts that the tenant is available on.
   *
   * This is used to determine the tenant based on the hostname.
   *
   * @example
   * ```ts
   * const tenant = getTenantIdByHost('zoocasa.com');
   * ```
   */
  hosts: string[];

  contentHosts: ContentHosts;
  /**
   * The blog API URL for the tenant. This is used by the WordPress API to fetch blog posts from the tenant's blog.
   *
   * @example
   * ```ts
   * const tenant = getTenantById(ZOOCASA_TENANT_ID);
   * const blogApiUrl = tenant.blogApiUrl;
   * const ZoocasaBlogPostsApi: BlogPostsApiType = createWordPressApi<typeof EMBEDDED_FEATURED_MEDIA, PostsType<EmbeddedFeaturedMedia>>(blogApiUrl);
   * ```
   */
  blogApiUrl?: string;
  blogContentPageUrl?: string;

  /**
   * The theme object for the tenant. This is only for styles and colors and we need to migrate to MUI theming.
   *
   * @example
   * ```ts
   * const tenant = getTenantById(ZOOCASA_TENANT_ID);
   * const colors = tenant.theme.colors;
   * ```
   */
  theme: T;
  features: Features;
  localization: TenantLocalizationConfig;
  config: C;
  routes: TenantRouteConfig;
  debug: {
    forceTenantHeaderValue: string;
    forceTenantFeatureValue: string;
  };
  googleTagManager: {
    containerIds: Record<G, string> & Record<typeof DEFAULT_GTM_CONTAINER_ID_KEY, string>;
  };
  sso: {
    googleWebClientId: string;
    facebookAppId: string;
  };
  sitemap: {
    urls: string[];
  };
  robots: {
    disallowDomains: string[];
    disallowPaths: string[];
  };
  pvr: {
    phoneNumber: string;
  };
  websiteUUID?: string;
  mobileAppBanner: MobileAppBannerConfig;
  consent: ConsentRule;
}>;
